import React, { useCallback, useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import {
    type TransactionResultDependencies,
    TransactionResultLogic,
} from './logic'
import { InvoiceResult } from '~/features/ui-components/invoice-result'
import type {
    DocumentTransactionAssociation,
    BankTransaction,
} from '~/storage/types'
import { Button } from '~common/components/button'
import {
    useCacheValue,
    useTransactionById,
    useMatchAssociationsForTransaction,
} from '~/cache-context'
import { SortFieldTransactions } from '../../logic'
import { MatchSearch } from '~/features/match-search'
import { LoadingIndicator } from '~common/components/loading'
import { CategorySelect } from '~/features/ui-components/category-select'
import { PossibleColumns } from '~/features/ui-components/invoice-result/logic'
import { FloatingPopout } from '~common/components/floating-popout'
import { TextField } from '~common/components/text-field'
import { Icon } from '~common/components/icon'

const defaultVisibleColumns = [
    'match',
    'date',
    'amount',
    'vendorName',
    'referenceText',
    'note',
    'createdAt',
] as const

export interface TransactionResultProps extends TransactionResultDependencies {
    dontShowMatches?: boolean
    context?: 'match-search'
    contextTransactionId?: string
    confirmedMatch?: boolean
    showAllConfirmedMatches?: boolean
    onToggleAllConfirmedMatches?: () => void
    transaction?: BankTransaction | null
}

export function TransactionResult(props: TransactionResultProps) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(TransactionResultLogic, {
        ...props,
        ...appContext,
    })

    // UI state
    const [isHovered, setIsHovered] = useState(false)
    const hasIndividualToggle = React.useRef(false)
    const noteCellRef = useRef<HTMLTableCellElement>(null)

    const transactionId = props.transactionDbId ?? ''
    const transactionFromStore = useTransactionById(transactionId)
    const transaction =
        transactionFromStore ?? props.transaction ?? undefined
    const matchingAssociations = useMatchAssociationsForTransaction(transactionId)

    const datevPluginEnabled =
        props.visibleColumns?.includes('category') ?? false
    const categoryMatching = useCacheValue('categoryMatching') as
        | Record<string, 'pristine' | 'running' | 'success' | 'error'>
        | undefined
    const cacheLoadState = useCacheValue('loadState')

    const isSnoozed = transaction?.status === 'snoozed'

    const formattedData = logic.formatTransactionData(
        {
            specificMatchConfidence: props.specificMatchConfidence,
            contextInvoiceId: props.contextInvoiceId,
        },
        transaction ?? undefined,
    )

    let specificConfidenceForMatch: number | undefined = undefined
    let hasUserConfirmedMatch = false
    let hasAlreadyBeenMatchedToOtherInvoice = false

    if (cacheLoadState === 'success') {
        for (const assoc of matchingAssociations) {
            if (assoc.method === 'user_confirmed') {
                if (assoc.documentId !== props.contextInvoiceId) {
                    hasAlreadyBeenMatchedToOtherInvoice = true
                }
                if (assoc.documentId === props.contextInvoiceId) {
                    hasUserConfirmedMatch = true
                }
            }
            if (assoc.method === 'auto' || assoc.method === 'user_confirmed') {
                if (assoc.documentId === props.contextInvoiceId) {
                    specificConfidenceForMatch = Math.round(assoc.confidence)
                }
            }
        }
    }

    const matchingInvoices = matchingAssociations

    const hasPotentialMatches = matchingInvoices.length > 0
    const isReferenced = props.contextInvoiceId != null

    const matchCellContent = () => {
        if (state.isProcessingFileDrop) {
            return (
                <ButtonRow>
                    <Button
                        icon={<LoadingIndicator size="small" />}
                        label="Importing"
                        variant="glass"
                        size="medium"
                    />
                </ButtonRow>
            )
        }

        const confirmMatchButton = (warning?: boolean) => {
            return (
                <Button
                    icon={<Icon name="check" size="small" />}
                    status={warning ? 'warning' : undefined}
                    onClick={handleConfirm}
                    variant="secondary"
                    size="small"
                    tooltip={
                        warning
                            ? 'Caution! \nAlready matched to other invoice'
                            : 'Confirm Match'
                    }
                />
            )
        }

        const removeMatchSuggestionButton = () => {
            return (
                <Button
                    icon="close"
                    tooltip="Remove match suggestion"
                    onClick={logic.removeMatch}
                    variant="secondary"
                    size="small"
                />
            )
        }

        const confirmedButton = () => {
            // Use local state if user has individually toggled, otherwise use global state if provided
            const showConfirmedMatches =
                props.showAllConfirmedMatches === undefined ||
                hasIndividualToggle.current
                    ? logic.state.showConfirmedMatches
                    : props.showAllConfirmedMatches

            return (
                <Button
                    label="Matched"
                    icon="check-circle"
                    onClick={(e) => {
                        e.stopPropagation()
                        if (e.shiftKey && props.onToggleAllConfirmedMatches) {
                            props.onToggleAllConfirmedMatches()
                            // Reset individual toggle when using global toggle
                            hasIndividualToggle.current = false
                        } else {
                            hasIndividualToggle.current = true
                            logic.toggleShowConfirmedMatches()
                        }
                    }}
                    variant="ghost"
                    size="small"
                    status="info"
                    tooltip={
                        !showConfirmedMatches
                            ? `Match Confirmed!\nClick to view\nShift+Click to toggle all`
                            : `Click to hide\nShift+Click to toggle all`
                    }
                />
            )
        }

        const confidenceDisplay = (align?: 'left' | 'center') => {
            if (specificConfidenceForMatch) {
                return (
                    <ConfidenceDisplay align={align}>
                        {specificConfidenceForMatch}%
                    </ConfidenceDisplay>
                )
            } else {
                return undefined
            }
        }

        const findMatchButton = () => {
            return (
                <Button
                    label="Find Match"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleFindMatch()
                    }}
                    variant="secondary"
                    icon="search"
                    size="small"
                />
            )
        }

        const removeMatchButton = () => {
            return (
                <Button
                    icon="close"
                    tooltip="Remove Match"
                    onClick={logic.resetMatch}
                    variant="secondary"
                    size="small"
                />
            )
        }

        const changeMatchButton = () => {
            if (props.contextInvoiceId) {
                return (
                    <Button
                        icon="search"
                        tooltip="Change"
                        onClick={handleFindMatch}
                        variant="secondary"
                        size="small"
                    />
                )
            }
        }

        const snoozeButton = () => {
            return (
                <Button
                    icon="bellz"
                    tooltip="Snooze transaction"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleSnooze(e)
                    }}
                    variant={isSnoozed ? 'outline' : 'secondary'}
                    status={isSnoozed ? 'info' : undefined}
                    size="small"
                />
            )
        }

        if (hasUserConfirmedMatch) {
            if (isReferenced) {
                if (isHovered) {
                    return (
                        <ButtonRow>
                            {removeMatchButton()}
                            {changeMatchButton()}
                        </ButtonRow>
                    )
                } else {
                    if (props.context !== 'match-search') {
                        return confidenceDisplay('center')
                    } else {
                        return <ButtonRow>{confirmedButton()}</ButtonRow>
                    }
                }
            } else {
                return <ButtonRow>{confirmedButton()}</ButtonRow>
            }
        } else {
            if (isReferenced) {
                if (isHovered) {
                    if (props.context == 'match-search') {
                        return (
                            <ButtonRow>
                                {confirmMatchButton(
                                    hasAlreadyBeenMatchedToOtherInvoice,
                                )}
                                {removeMatchSuggestionButton()}
                            </ButtonRow>
                        )
                    }
                    return (
                        <ButtonRow>
                            {confirmMatchButton(
                                hasAlreadyBeenMatchedToOtherInvoice,
                            )}
                            {removeMatchSuggestionButton()}
                            {confidenceDisplay()}
                        </ButtonRow>
                    )
                }
                return <ButtonRow>{confidenceDisplay('center')}</ButtonRow>
            } else if (hasAlreadyBeenMatchedToOtherInvoice) {
                return <ButtonRow>{confirmedButton()}</ButtonRow>
            } else {
                if (isHovered) {
                    return (
                        <ButtonRow>
                            {snoozeButton()}
                            {findMatchButton()}
                        </ButtonRow>
                    )
                } else {
                    if (isSnoozed) {
                        return snoozeButton()
                    }
                }
            }
        }
    }

    const renderCell = (
        field: PossibleColumns,
        widthKey: SortFieldTransactions,
    ) => {
        const typedField =
            field === 'match' ? null : (field as keyof typeof formattedData)
        if (formattedData === null) {
            return null
        }
        const value = typedField ? formattedData[typedField] : ''
        switch (field) {
            case 'match':
                return <TableCell key={field}>{matchCellContent()}</TableCell>
            case 'amount':
                return (
                    <TableCell key={field}>
                        <TableCellContent>
                            <Currency>{formattedData.currency}</Currency>
                            <Value bold>{value}</Value>
                        </TableCellContent>
                    </TableCell>
                )
            case 'category':
                if (props.isReferenced) {
                    return <TableCell key={field}></TableCell>
                }
                if (!datevPluginEnabled) {
                    return null
                }
                return (
                    <TableCell key={field}>
                        <TableCellContent>
                            {props.transactionDbId &&
                            categoryMatching?.[props.transactionDbId] ===
                                'running' ? (
                                <LoadingIndicator size="small" />
                            ) : null}
                            <CategorySelect
                                initValue={transaction?.category}
                                onChange={async (categoryId) => {
                                    if (
                                        props.transactionDbId &&
                                        appContext.services.bankTransaction
                                    ) {
                                        await appContext.services.bankTransaction.update(
                                            props.transactionDbId,
                                            { category: categoryId },
                                        )
                                    }
                                }}
                                variant="tableCell"
                                transactionId={
                                    props.transactionDbId ?? undefined
                                }
                                size="small"
                            />
                        </TableCellContent>
                    </TableCell>
                )
            case 'date':
            case 'vendorName':
            case 'referenceText':
            case 'createdAt':
                return (
                    <TableCell key={field}>
                        <Value bold={false}>{value}</Value>
                    </TableCell>
                )
            case 'note':
                return (
                    <TableCell
                        onClick={(e) => {
                            e.stopPropagation()
                        }}
                        key={field}
                        ref={noteCellRef}
                    >
                        <FloatingPopout
                            trigger={
                                <NoteCellContent
                                    onClick={(e) => {
                                        e.stopPropagation()
                                    }}
                                >
                                    <TextField
                                        editable={false}
                                        controlledValue={value || ''}
                                        size="medium"
                                        variant="tableCell"
                                        onClick={() => {
                                            logic.openNotePopup(value || '')
                                        }}
                                    />
                                </NoteCellContent>
                            }
                            content={
                                <NotePopupContent>
                                    <TextField
                                        multiline
                                        editable
                                        initValue={logic.state.noteEditValue}
                                        onChange={(newValue) => {
                                            logic.setNoteEditValue(newValue)
                                        }}
                                        onClear={() => {
                                            logic.setNoteEditValue('')
                                        }}
                                        onConfirm={(newValue) => {
                                            logic.closeNotePopup(newValue)
                                        }}
                                        debounceMs={0}
                                        placeholder="Add a note..."
                                        size="small"
                                        resizable="vertical"
                                        autoFocus
                                        height="200px"
                                        confirmOnBlur
                                    />
                                </NotePopupContent>
                            }
                            open={logic.state.isNotePopupOpen}
                            onOpenChange={(open) => {
                                if (open) {
                                    logic.openNotePopup(value || '')
                                } else {
                                    logic.closeNotePopup(value || '')
                                }
                            }}
                            placement="bottom-start"
                            offset={4}
                        />
                    </TableCell>
                )
            default:
                return null
        }
    }

    if (formattedData === null) {
        return null
    }

    const handleClickOnResult = () => {
        if (props.onClick) {
            props.onClick()
            return
        }

        if (props.transactionDbId) {
            handleFindMatch()
        }
    }

    const handleFindMatch = () => {
        if (props.transactionDbId && transaction) {
            appContext.services.globalEvents.emit({
                type: 'spawnPane',
                render: () => (
                    <MatchSearch
                        rootItem={transaction}
                        itemType="transaction"
                        title="Find matching invoices for"
                    />
                ),
                title: 'Find matching invoices for',
                matchSearchProps: {
                    rootItem: transaction,
                    itemType: 'transaction',
                    title: 'Find matching invoices for',
                },
            })
        }
    }

    const handleSnooze = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation()
            if (props.transactionDbId && appContext.services.bankTransaction) {
                const newStatus = isSnoozed ? 'active' : 'snoozed'
                await appContext.services.bankTransaction.update(
                    props.transactionDbId,
                    { status: newStatus },
                )
            }
        },
        [props.transactionDbId, isSnoozed, appContext],
    )

    const handleConfirm = () => {
        if (!props.transactionDbId || !props.contextInvoiceId) return
        appContext.services.transactionInvoiceMatching?.confirmAssociation(
            props.contextInvoiceId,
            props.transactionDbId,
        )
    }

    const visibleColumns = props.visibleColumns || defaultVisibleColumns

    const showMatchColumn = !props.dontShowMatches // Assuming state.showMatchColumn is true by default
    let subColSpan = 7
    const subDontShowMatches = !showMatchColumn

    const renderReferencedInvoices = () => {
        if (props.dontShowMatches || !matchingInvoices) {
            return null
        }

        if (cacheLoadState === 'running') {
            return (
                <SubInvoiceRow>
                    <SubInvoiceCell colSpan={subColSpan}>
                        <NoData>Loading matching invoices...</NoData>
                    </SubInvoiceCell>
                </SubInvoiceRow>
            )
        }

        if (cacheLoadState === 'error') {
            return (
                <SubInvoiceRow>
                    <SubInvoiceCell colSpan={subColSpan}>
                        <NoData>Error loading matching invoices</NoData>
                    </SubInvoiceCell>
                </SubInvoiceRow>
            )
        }

        // Use local state if user has individually toggled, otherwise use global state if provided
        const showConfirmedMatches =
            props.showAllConfirmedMatches === undefined ||
            hasIndividualToggle.current
                ? logic.state.showConfirmedMatches
                : props.showAllConfirmedMatches
        const shouldHideConfirmed =
            hasUserConfirmedMatch && !showConfirmedMatches

        if (shouldHideConfirmed) {
            return null
        }

        const sortedMatchingTransactions = matchingInvoices.sort(
            (a, b) => b.confidence - a.confidence,
        )

        const userConfirmedMatches = sortedMatchingTransactions.filter(
            (assoc) => assoc.method === 'user_confirmed',
        )

        const subRows: React.ReactNode[] = []

        if (userConfirmedMatches.length > 0 && !showConfirmedMatches) {
            return null
        }

        const renderInvoiceRow = (documentId: string, confidence: number) => {
            let visibleColumns: PossibleColumns[] = [
                'match',
                'date',
                'amount',
                'vendorName',
                'reason',
                'note',
                'createdAt',
            ]

            if (datevPluginEnabled) {
                visibleColumns = [
                    'match',
                    'date',
                    'amount',
                    'vendorName',
                    'reason',
                    'note',
                    'category',
                    'createdAt',
                ]
            }

            return (
                <InvoiceResult
                    invoiceDbId={documentId}
                    referenceLocation={'transactions'}
                    hideAvatar={props.hideAvatar}
                    contextTransactionId={props.transactionDbId ?? undefined}
                    specificMatchConfidence={confidence}
                    dontShowMatches={subDontShowMatches}
                    visibleColumns={visibleColumns as PossibleColumns[]}
                />
            )
        }

        if (userConfirmedMatches.length > 0) {
            userConfirmedMatches.forEach(({ documentId, confidence }) => {
                subRows.push(renderInvoiceRow(documentId, confidence))
            })
        }

        const autoMatches = sortedMatchingTransactions.filter(
            (assoc) => assoc.method === 'auto',
        )

        const topMatch = autoMatches[0]
        const restMatches = autoMatches.slice(1)

        if (
            topMatch &&
            (state.showAllMatches || userConfirmedMatches.length === 0)
        ) {
            subRows.push(
                renderInvoiceRow(topMatch.documentId, topMatch.confidence),
            )
        }
        if (restMatches.length > 0 && state.showAllMatches) {
            restMatches.forEach(({ documentId, confidence }) => {
                subRows.push(renderInvoiceRow(documentId, confidence))
            })
        }

        if (
            autoMatches.length > 1 ||
            (topMatch && userConfirmedMatches.length > 0)
        ) {
            const buttonText = state.showAllMatches
                ? 'Hide all matches'
                : 'Show All Matches'
            subRows.push(
                <SubInvoiceRow>
                    <SubInvoiceCell colSpan={subColSpan}>
                        <ButtonRowTransactionMatches>
                            <Button
                                label={buttonText}
                                variant="ghost"
                                size="small"
                                onClick={() => logic.setShowAllMatches()}
                            />
                        </ButtonRowTransactionMatches>
                    </SubInvoiceCell>
                </SubInvoiceRow>,
            )
        }

        return subRows
    }

    return (
        <>
            <TableRowContainer
                onClick={(event) => {
                    event.stopPropagation()
                    handleClickOnResult()
                }}
                onDragOver={(e) => {
                    if (e.dataTransfer?.types?.includes('Files')) {
                        e.preventDefault()
                        setIsHovered(true)
                    }
                }}
                onDragLeave={(e) => {
                    if (e.dataTransfer?.types?.includes('Files')) {
                        e.preventDefault()
                        setIsHovered(false)
                    }
                }}
                onDrop={async (e) => {
                    if (!props.transactionDbId) return
                    const file = e.dataTransfer?.files?.[0]
                    if (!file) return
                    e.preventDefault()
                    e.stopPropagation()
                    await logic.processDroppedFileOnRow(file)
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                isReferenced={props.isReferenced}
                confirmedMatch={hasUserConfirmedMatch}
                isImporting={state.isProcessingFileDrop}
            >
                {visibleColumns.map((field) => {
                    const widthKey =
                        field === 'match'
                            ? 'match'
                            : field === 'vendorName'
                              ? 'vendorName'
                              : field === 'referenceText'
                                ? 'referenceText'
                                : field === 'note'
                                  ? 'note'
                                  : field === 'createdAt'
                                    ? 'createdAt'
                                    : field === 'date'
                                      ? 'date'
                                      : field === 'amount'
                                        ? 'amount'
                                        : field
                    return renderCell(
                        field as PossibleColumns,
                        widthKey as SortFieldTransactions,
                    )
                })}
            </TableRowContainer>
            {!props.isReferenced &&
                props.context !== 'match-search' &&
                renderReferencedInvoices()}
        </>
    )
}

const TableRowContainer = styled.tr<{
    onClick?: (event: React.MouseEvent) => void
    isReferenced?: boolean
    confirmedMatch?: boolean
    isImporting?: boolean
}>`
    box-sizing: border-box;
    height: ${({ theme }) => theme.spacing[16]};
    ${({ onClick }) =>
        onClick &&
        css`
            cursor: pointer;
            &:hover {
                background: ${({ theme }) => theme.colors.background.bg2};
            }
        `}
    ${({ isReferenced }) =>
        isReferenced &&
        css`
            background: ${({ theme }) => theme.colors.background.bg2};
            height: ${({ theme }) => theme.spacing[10]};

            & > td {
                padding-top: 0;
                padding-bottom: 0;
            }
        `}
    ${({ confirmedMatch, isReferenced }) =>
        confirmedMatch &&
        isReferenced &&
        css`
            box-sizing: border-box;
            opacity: 0.6;
        `}

    ${({ isImporting }) =>
        isImporting &&
        css`
            background: ${({ theme }) => theme.colors.background.bg3};
        `}
`

const Value = styled.div<{ bold?: boolean }>`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme, bold }) =>
        bold
            ? theme.typography.fontWeight.medium
            : theme.typography.fontWeight.light};
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
`

const NoData = styled.div`
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    text-align: center;
    padding: ${({ theme }) => theme.spacing[2]};
`

const Currency = styled.div`
    color: ${({ theme }) => theme.colors.greyScale[100]};
    width: 10px;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

const TableCell = styled.td`
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    vertical-align: middle;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative; // Allow absolute positioning for button in cells
`

const TableCellContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing[1]};
`

const SubInvoiceRow = styled.tr``

const SubInvoiceCell = styled.td`
    padding: 0;
`

const ButtonRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const ConfidenceDisplay = styled.span<{ align?: 'left' | 'center' }>`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    ${({ align }) =>
        align === 'center' &&
        css`
            padding-left: ${({ theme }) => theme.spacing[4]};
        `}
`

const ButtonRowTransactionMatches = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    margin-top: ${({ theme }) => theme.spacing[2]};
    margin-left: ${({ theme }) => theme.spacing[2]};
`

const NoteCellContent = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 30px;
    &:hover {
        opacity: 0.8;
        background: ${({ theme }) => theme.colors.background.bg5};
    }
`

const NotePopupContent = styled.div`
    min-width: 300px;
    min-height: 200px;
    max-width: 500px;
    padding: ${({ theme }) => theme.spacing[1]};
`
